import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-50 bg-[var(--bg)] border-b transition-colors ${
        scrolled || open ? "border-stone-300" : "border-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 flex items-center justify-between py-1">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_nonprofit-hub-62/artifacts/5v3f7h8z_ECC-logo-r3.png"
            alt="Election Competition Center"
            className="h-[144px] w-[144px] object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav
          data-testid="desktop-nav"
          className="hidden md:flex items-center gap-1 sm:gap-2"
        >
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="px-3 sm:px-4 py-2 text-sm text-stone-700 hover:text-stone-900 hover:bg-stone-200/70 transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Mobile menu button */}
        <button
          type="button"
          data-testid="mobile-menu-button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="md:hidden inline-flex items-center justify-center h-11 w-11 border border-stone-300 text-stone-900 hover:bg-stone-200/70 transition-colors"
        >
          {open ? (
            <X className="w-5 h-5" strokeWidth={1.75} />
          ) : (
            <Menu className="w-5 h-5" strokeWidth={1.75} />
          )}
        </button>
      </div>

      {/* Mobile dropdown panel */}
      {open && (
        <nav
          data-testid="mobile-nav"
          className="md:hidden border-t border-stone-300 bg-[var(--bg)]"
        >
          <ul className="max-w-6xl mx-auto px-6 sm:px-10 py-2 flex flex-col">
            {links.map((l) => (
              <li key={l.to} className="border-b border-stone-200 last:border-b-0">
                <Link
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="block py-4 font-serif text-2xl text-stone-900 hover:text-stone-600 transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}
